/**
 * IvyDock — portal-mounted persistent chat surface (Phase 2D).
 *
 * Mounted once at App root (below <Routes>, above the render boundary), so it
 * survives all route changes and reliably unifies what were previously two
 * separate IvyChat instances (MainLayout overlay + /ivy full-page). Consumes
 * PageFocusContext + route via useIvyPageContext so every message carries the
 * user's current focus; consumes IvyDockContext for open/closed state so
 * Sidebar + IvyPage can drive visibility via a typed API (not DOM events).
 *
 * Phase 3 additions:
 *   - Intent executor wired after every response (Tier 1 immediate, Tier 2 modal)
 *   - statusBanner state drives AlertBanner inside the dock (3s auto-dismiss)
 *   - pendingConfirmation state drives IvyConfirmationModal (portal-mounted)
 *   - useNavigate() passed to executor as navigator function
 *   - Intent block stripped from content before storing in messages
 *
 * State ownership (all local here):
 *   - sessionId          — regenerated on "New conversation"
 *   - messages           — chat log for the current session
 *   - loading            — blocking request in flight
 *   - error              — last network error (cleared on next send)
 *   - statusBanner       — transient AlertBanner state (Phase 3)
 *   - pendingConfirmation — Tier 2 confirmation state (Phase 3)
 *
 * The `input` buffer stays in IvyChat (purely presentational).
 *
 * Portal target: document.body. Escapes all parent stacking contexts so the
 * dock sits reliably above MainLayout's right-panel asides and any future
 * page-level modal. z-index 200 matches the existing .ivy-float-fab from 2A.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router'
import { usePodcast } from '../contexts/PodcastContext'
import { sendIvyMessage, endIvySession } from '../lib/api'
import { config } from '../lib/config'
import { useIvyPageContext } from '../hooks/useIvyPageContext'
import { useIvyDock } from '../contexts/IvyDockContext'
import { GraspPodIvyIcon } from './icons/GraspPodIcons'
import IvyChat, { type ChatMessage } from './IvyChat'
import AlertBanner from './AlertBanner'
import IvyConfirmationModal from './IvyConfirmationModal'
import { executeIntent, type StatusBanner, type PendingConfirmation } from '../lib/ivyIntentExecutor'
import { stripIntentBlock } from '../lib/ivyClientParser'

function generateSessionId(): string {
  return `ivy-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export default function IvyDock() {
  const { selectedPodcast } = usePodcast()
  const pageContext = useIvyPageContext()
  const { isOpen, openDock, closeDock } = useIvyDock()
  const navigate = useNavigate()

  const [sessionId, setSessionId] = useState<string>(generateSessionId)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statusBanner, setStatusBanner] = useState<StatusBanner | null>(null)
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null)

  // Auto-dismiss statusBanner after 3 seconds
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!statusBanner) return
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current)
    bannerTimerRef.current = setTimeout(() => setStatusBanner(null), 3000)
    return () => {
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current)
    }
  }, [statusBanner])

  // Phase 4: beforeunload + visibilitychange beacon — fire session-end on
  // tab close / navigation away so synthesis runs even without "New conversation".
  // sendBeacon is best-effort; if auth fails the nightly deep reprocess catches it.
  const sessionIdRef = useRef(sessionId)
  useEffect(() => { sessionIdRef.current = sessionId }, [sessionId])

  useEffect(() => {
    if (!selectedPodcast) return

    function sendBeacon() {
      const sid = sessionIdRef.current
      if (!sid) return
      const url = `${config.apiBase}/ivy/session/beacon`
      const body = JSON.stringify({ sessionId: sid })
      try {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
      } catch {
        // sendBeacon not available (SSR guard, old browser) — no-op
      }
    }

    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') sendBeacon()
    }

    window.addEventListener('beforeunload', sendBeacon)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.removeEventListener('beforeunload', sendBeacon)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [selectedPodcast])

  const handleSend = useCallback(
    async (text: string) => {
      if (!text.trim() || loading || !selectedPodcast) return
      const userMsg: ChatMessage = { role: 'user', content: text.trim() }
      const updated = [...messages, userMsg]
      setMessages(updated)
      setLoading(true)
      setError(null)
      // Clear banner on new user message
      setStatusBanner(null)

      try {
        const response = await sendIvyMessage(
          updated,
          sessionId,
          selectedPodcast.id,
          pageContext,
        )

        // Strip intent block before storing in message history (keep display clean)
        const displayContent = stripIntentBlock(response.content)
        setMessages((prev) => [...prev, { role: 'assistant', content: displayContent }])

        // Dispatch intent if present
        if (response.intent) {
          await executeIntent(response.intent, {
            navigate,
            sessionId,
            podcastId: selectedPodcast.id,
            setStatusBanner,
            setPendingConfirmation,
          })
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to get response')
      } finally {
        setLoading(false)
      }
    },
    [loading, messages, sessionId, selectedPodcast, pageContext, navigate],
  )

  const handleNewSession = useCallback(async () => {
    if (messages.length > 0) {
      try {
        await endIvySession(sessionId)
      } catch {
        /* best-effort */
      }
    }
    setMessages([])
    setError(null)
    setStatusBanner(null)
    setPendingConfirmation(null)
    setSessionId(generateSessionId())
  }, [messages.length, sessionId])

  // Portal target — SSR-safe
  if (typeof document === 'undefined') return null

  // Auth/podcast gate
  if (!selectedPodcast) return null

  const dockMarkup = isOpen ? (
    <div
      className="ivy-dock-panel fixed bottom-6 right-6 w-[400px] h-[600px] max-h-[80vh] bg-[#0E0E1A] border border-[rgba(167,139,250,0.20)] rounded-2xl overflow-hidden shadow-2xl flex flex-col"
      role="dialog"
      aria-label="Ivy chat"
    >
      {/* Status banner — inside dock, above chat area */}
      {statusBanner && (
        <div className="px-3 pt-3 shrink-0">
          <AlertBanner
            type={statusBanner.type}
            message={statusBanner.message}
            onDismiss={() => setStatusBanner(null)}
          />
        </div>
      )}

      <IvyChat
        messages={messages}
        loading={loading}
        error={error}
        onSend={handleSend}
        onNewSession={handleNewSession}
        onClose={closeDock}
      />
    </div>
  ) : (
    <button
      onClick={openDock}
      className="ivy-float-fab hidden md:flex"
      title="Ask Ivy"
      aria-label="Open Ivy chat"
    >
      <GraspPodIvyIcon size={28} active />
    </button>
  )

  return (
    <>
      {createPortal(dockMarkup, document.body)}
      {/* Tier 2 confirmation modal — portal-mounted separately so it sits above the dock */}
      {pendingConfirmation &&
        createPortal(
          <IvyConfirmationModal
            message={pendingConfirmation.message}
            actionLabel={pendingConfirmation.actionLabel}
            onConfirm={pendingConfirmation.onConfirm}
            onDismiss={() => setPendingConfirmation(null)}
          />,
          document.body
        )}
    </>
  )
}
